import { insertNumber } from './keyBuffer';
import { selfUri as selfUriProd } from './environment-prod';
import { selfUri as selfUriTest } from './environment-test';
import { selfUri as selfUriStaging } from './environment-staging';
import { selfUri as selfUriDev } from './environment-dev';
import React, { FC, PropsWithChildren } from 'react';
import { throttleInteraction } from './utils/ui-helpers';
import { useEventListener } from './hooks/useEventListener';
import { ApplicationResolution } from './applicationResolution';
import { isDevelopmentEnvironment } from './utils/env-utils';
import { Keys } from './utils/keys';
import { useProfilesLocalStorage } from './ProfilesLocalStorageContext';
import { isParentProfile, isSubProfile } from './typeGuards';
import { isClient } from '../common/utils';
import { ps5Mode } from '../common/types';
import { useNavigateWithHistory } from './Routes/useNavigateWithHistory';
import { getLoginRoutePath, routePathsById } from './Routes/getRoutePath';
import { useLocationWithState } from './Routes/useLocationWithState';
import {
  ADD_SUB_PROFILE_PATH,
  CONSENT_PATH,
  DEVICE_INFO_PATH,
  LOGIN_ONBOARDING_PATH,
  SEARCH_PATH,
  TESTS_PATH,
} from './Routes/routeNames';
import { useAppFeaturesContext } from './contexts/AppFeaturesContext/AppFeaturesContext';

/**
   Wrapper to input numbers with a remote to run some useful testing utilities.
   Should be called inside AppFeatures provider and Redux Provider.
   Testing codes are disabled in views which include user input.
 */
const GlobalTestingCodes: FC<PropsWithChildren> = ({ children }) => {
  const { mode, applicationResolution } = useAppFeaturesContext();
  const reactRouterLocation = useLocationWithState();

  const isPageTakingInput =
    reactRouterLocation.pathname === `/${SEARCH_PATH}` ||
    reactRouterLocation.pathname.startsWith(getLoginRoutePath());

  const { profiles } = useProfilesLocalStorage();

  const reactRouterNavigate = useNavigateWithHistory();

  const keyHandler = throttleInteraction((e: KeyboardEvent) => {
    const keyCode =
      mode === ps5Mode && e.keyCode > 115 ? e.keyCode - 67 : e.keyCode;

    switch (keyCode) {
      case Keys.VK_0:
      case Keys.VK_1:
      case Keys.VK_2:
      case Keys.VK_3:
      case Keys.VK_4:
      case Keys.VK_5:
      case Keys.VK_6:
      case Keys.VK_7:
      case Keys.VK_8:
      case Keys.VK_9: {
        const code = insertNumber(keyCode);
        const localUrl = window.location.hostname.replace(
          'areena.yle',
          'areena.local.yle'
        );

        if (code === '3000') {
          window.location.href = `http://${localUrl}:3000/?resolution=${applicationResolution}`;
        } else if (code === '8900') {
          window.location.href = `http://${localUrl}:8900/?resolution=${applicationResolution}`;
        } else if (code === '2121') {
          const testEnvironmentUri = selfUriTest[mode];
          if (testEnvironmentUri) {
            window.location.href = `${testEnvironmentUri}/?resolution=${applicationResolution}`;
          }
        } else if (code === '3232') {
          const stagingEnvironmentUri = selfUriStaging[mode];
          if (stagingEnvironmentUri) {
            window.location.href = `${stagingEnvironmentUri}/?resolution=${applicationResolution}`;
          }
        } else if (code === '3434') {
          const devEnvironmentUri = selfUriDev[mode];
          if (devEnvironmentUri) {
            window.location.href = `${devEnvironmentUri}/?resolution=${applicationResolution}`;
          }
        } else if (code === '5153') {
          reactRouterNavigate(`/${CONSENT_PATH}`);
        } else if (code === '0236') {
          reactRouterNavigate(`/${DEVICE_INFO_PATH}`);
        } else if (code === '1416') {
          reactRouterNavigate(`/${TESTS_PATH}`);
        } else if (code === '8880') {
          window.location.href = `${window.location.href}?resolution=${applicationResolution}`;
        } else if (code === '1080') {
          window.location.href = `/?resolution=${ApplicationResolution.FULLHD}`;
        } else if (code === '98042963') {
          const areenaAppUri = selfUriProd[mode];
          if (areenaAppUri) {
            window.location.href = `${areenaAppUri}/?resolution=${applicationResolution}`;
          }
        } else if (code === '99052963') {
          const areenaTestAppUri = selfUriTest[mode];
          if (areenaTestAppUri) {
            window.location.href = `${areenaTestAppUri}/?resolution=${applicationResolution}`;
          }
        } else if (code === '1199') {
          // audio vod test: Pikku Kakkosen Satuaarteet
          reactRouterNavigate(routePathsById.item('1-4580180'));
        } else if (code === '1051') {
          reactRouterNavigate(`/${LOGIN_ONBOARDING_PATH}`);
        } else if (code === '443322') {
          reactRouterNavigate(`/${ADD_SUB_PROFILE_PATH}`);
        } else if (code === '554411') {
          const account = profiles.find(isParentProfile);

          if (account) {
            reactRouterNavigate(routePathsById.editProfile(account.userId));
          }
        } else if (code === '554422') {
          const profile = profiles.find(isSubProfile);

          if (profile) {
            reactRouterNavigate(routePathsById.editProfile(profile.userId));
          }
        }
        break;
      }
    }

    if (!isDevelopmentEnvironment(window.location.hostname)) {
      e.preventDefault(); // Guarding against Telia STB weirdness, be sure before you remove
    }
  });

  const safeKeyHandler = isPageTakingInput ? () => {} : keyHandler;

  const listenerTarget = isClient() ? document : undefined;

  useEventListener('keydown', safeKeyHandler, listenerTarget);

  return <>{children}</>;
};

export default GlobalTestingCodes;
